import { useQuery } from "@apollo/client";
import { ArrowForward } from "@mui/icons-material";
import { IconButton, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Error from "../common/Error";
import Loading from "../common/Loading";
import { pages } from "../pages/pages";
import { Setting, SettingsQueryData } from "../types";
import LoadMoreButton from "./LoadMoreButton";
import { countedSettingsQuery } from "./Profile.gql";
import LoadLessButton from "./ShowLessButton";

const Settings: React.FunctionComponent = () => {
    const { t } = useTranslation();
    const [isLoadingMore, setIsLoadingMore] = React.useState(false);
    const navigate = useNavigate();

    const { data, loading, fetchMore } = useQuery(countedSettingsQuery, {
        variables: {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            userId: localStorage.getItem("userId")!,
            limit: 3,
            offset: 0,
        },
        fetchPolicy: "network-only",
    });

    const onLoadMore = () => {
        setIsLoadingMore(true);
        fetchMore({
            variables: {
                limit: 3,
                offset: data.countedSettings.nodes.length,
            },
            updateQuery: (previousResult: SettingsQueryData, { fetchMoreResult }: { fetchMoreResult?: SettingsQueryData | undefined }) => {
                setIsLoadingMore(false);
                const previousData = previousResult.countedSettings;
                const newData = fetchMoreResult?.countedSettings ?? { nodes: [], totalCount: 0, totalDuration: 0 };

                return {
                    countedSettings: {
                        ...previousData,
                        nodes: [...previousData.nodes, ...newData.nodes],
                    },
                };
            },
        });
    };

    const onLoadLess = () => {
        fetchMore({
            variables: {
                limit: 3,
                offset: 0,
            },
            updateQuery: (previousResult: SettingsQueryData, { fetchMoreResult }: { fetchMoreResult?: SettingsQueryData | undefined }) => {
                setIsLoadingMore(false);

                const newData = fetchMoreResult?.countedSettings ?? { nodes: [], totalCount: 0, totalDuration: 0 };

                return {
                    countedSettings: {
                        ...newData,
                    },
                };
            },
        });
    };

    return (
        <>
            {loading ? (
                <Loading />
            ) : data ? (
                data.countedSettings.nodes.length === 0 ? (
                    <>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                            <Typography color="primary" fontWeight="700" style={{ marginTop: 20, marginBottom: 10, fontSize: 20 }}>
                                {t("saved_settings")}
                            </Typography>
                        </div>
                        <Typography textAlign="center">{t("no_saved_settings")}</Typography>
                    </>
                ) : (
                    <>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                            <Typography color="primary" fontWeight="700" style={{ marginTop: 20, marginBottom: 10, fontSize: 20 }}>
                                {t("saved_settings")}
                            </Typography>
                            <Typography style={{ fontSize: 18 }}>
                                {data.countedSettings.nodes.length}/{data.countedSettings.totalCount}
                            </Typography>
                        </div>
                        <ul>
                            {data.countedSettings.nodes.map((setting: Setting) => (
                                <li key={setting.id}>
                                    <div style={{ display: "flex", marginTop: 10, alignItems: "center" }}>
                                        {setting.name}
                                        <IconButton onClick={() => navigate(pages.setting, { state: { setting } })}>
                                            <ArrowForward />
                                        </IconButton>
                                    </div>
                                </li>
                            ))}
                        </ul>

                        {data.countedSettings.nodes.length < data.countedSettings.totalCount && (
                            <LoadMoreButton isLoadingMore={isLoadingMore} onLoadMore={onLoadMore} />
                        )}

                        {data.countedSettings.nodes.length > 3 && data.countedSettings.nodes.length <= data.countedSettings.totalCount && (
                            <LoadLessButton onLoadLess={onLoadLess} />
                        )}
                    </>
                )
            ) : (
                <Error text={t("error")} />
            )}
        </>
    );
};

export default Settings;
